import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, X, ChevronRight } from 'lucide-react';
import { useProfile } from '../context/ProfileContext';
import { STEPS, isStepComplete } from '../pages/Onboarding';

/**
 * ProfileDepthNudge — subtle banner on Dashboard that appears for users who
 * triggered `completedOnboarding=true` (either by finishing Quickstart or
 * by Skip-for-now) but still have unanswered onboarding steps.
 *
 * Framed as "Enferno gets smarter the more you tell it" — NOT as a
 * paywall-style "unlock features" gate. Profile depth is purely about
 * recommendation fit.
 *
 * Counts ALL 8 non-review steps (Quickstart + Continuation). Uses
 * `STEPS` + `isStepComplete()` from Onboarding.jsx as source-of-truth.
 *
 * Deep-links to /onboarding/full → Onboarding.jsx field-aware-skips
 * already-completed steps, so the user lands on what they haven't done.
 */

const DISMISS_KEY = 'enferno_profile_depth_nudge_dismissed';

const ProfileDepthNudge = () => {
  const { profile } = useProfile();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try { setDismissed(sessionStorage.getItem(DISMISS_KEY) === '1'); } catch { /* ignore */ }
  }, []);

  const { remaining } = useMemo(() => {
    // Count every step EXCEPT review (review is the final confirmation
    // screen, not a "question"). Quickstart steps + continuation steps both
    // count here so the math stays correct no matter where the user is.
    const trackedSteps = STEPS.filter((s) => s.id !== 'review');
    const r = trackedSteps.filter((s) => !isStepComplete(s.id, profile || {})).length;
    return { remaining: r };
  }, [profile]);

  // Hide when:
  //  - Quickstart not done yet (IncompleteProfileHero handles that case)
  //  - Profile depth is fully filled (remaining === 0)
  //  - User dismissed for the session
  if (!profile?.completedOnboarding) return null;
  if (remaining === 0) return null;
  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
  };

  return (
    <div
      className="mb-5 relative overflow-hidden rounded-xl border border-enf-accent-fg/30 bg-enf-surface-deep p-4 sm:p-5"
      data-testid="profile-depth-nudge"
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-enf-accent/15 border border-enf-accent-fg/30 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-enf-accent-fg" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-enf-accent-fg font-semibold mb-1">
            Stoke the Enferno
          </div>
          <div className="text-base font-semibold text-enf-text-primary leading-snug mb-1">
            {remaining} {remaining === 1 ? 'question' : 'questions'} left to finish setup
          </div>
          <p className="text-xs text-enf-text-secondary leading-snug mb-3">
            Enferno gets smarter the more you tell it. Every detail makes your programs fit better — and you can stop and pick up where you left off any time.
          </p>
          <div className="flex items-center gap-2">
            <Link
              to="/onboarding/full"
              data-testid="profile-depth-nudge-continue"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-enf-accent hover:bg-enf-accent-hover text-enf-accent-contrast rounded-md text-xs font-semibold uppercase tracking-wider transition-colors"
            >
              Continue setup <ChevronRight className="w-3.5 h-3.5" />
            </Link>
            <button
              onClick={handleDismiss}
              data-testid="profile-depth-nudge-dismiss"
              className="px-2 py-1.5 text-xs text-enf-text-muted hover:text-enf-text-primary"
            >
              Later
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="p-1 text-enf-text-muted hover:text-enf-text-primary flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ProfileDepthNudge;
